"use client";

import { c as _c } from "react/compiler-runtime";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { hasReorderBounceParam, parseReorderParams, REORDER_STRIP_PARAMS, type ReorderIntent, type ReorderParamsResult, withReorderBounceParams } from "@/lib/reorder/oneClickReorderParams";
import { clearReorderIntent, getReorderIntent, hasBouncedReorderIntent, markReorderIntentBounced, setReorderIntent } from "@/lib/reorder/reorderIntent";
import { toSafeStrippedPath } from "@/lib/url/safePath";
import { useOneClickReorder } from "@/mutations/useOneClickReorder";
import { useAuthClaimsStore } from "@/stores/useAuthClaimsStore";
import { useNewCartStore } from "@/stores/useNewCartStore";
import { useServicesStore } from "@/stores/useServicesStore";
type AuthGate = "ready" | "bounce" | "giveUp";

/**
 * What the mount point paints while the reorder is in the air.
 * "idle" is also what a malformed link reports — a bad link shows nothing.
 */
export type OneClickReorderStatus = "idle" | "preparing" | "failed";

/**
 * What the flow holds in memory when sessionStorage refused the intent: on that path this and
 * the URL are the only copies, so it has to carry the bounce flag the stored intent would
 * normally have carried too.
 */
interface UnpersistedIntent {
  intent: ReorderIntent;
  bounced: boolean;
}

/** Every place the resume path may still find an intent, once the URL has none. */
interface ResumeSources {
  abandoned: boolean;
  identity: string | null;
  unpersisted: ReorderIntent | null;
}

/** Strips nothing — `toSafeStrippedPath` still resolves and origin-guards the path. */
const KEEP_ALL_PARAMS: readonly string[] = [];

/**
 * Three distinct auth states, not two. A session that exists but carries no
 * sg_customer_id is real (see src/lib/auth0.ts): bouncing it to Auth0 re-authenticates
 * silently and comes straight back, forever. And an intent that has already been through
 * the bounce once gives up rather than starting the loop over.
 *
 * hasBounced is a parameter rather than a `hasBouncedReorderIntent()` call in here, so the
 * sessionStorage read is visible at the call site instead of hiding inside a predicate.
 */
function resolveAuthGate(hasSession: boolean, sgCustomerId: string | undefined, hasBounced: boolean): AuthGate {
  if (sgCustomerId) return "ready";
  if (hasSession) return "giveUp";
  if (hasBounced) return "giveUp";
  return "bounce";
}

/**
 * What this pass should redeem, if anything. The three parser outcomes are three different
 * answers: a valid link wins outright, a malformed one yields nothing at all, and only the
 * absence of any reorder key falls back to what is still redeemable elsewhere.
 *
 * `abandoned` prevents loop of bad link redeeming previously set item/coupon
 */
function selectIntent(result: ReorderParamsResult, sources: ResumeSources): ReorderIntent | null {
  if (result.status === "valid") return result.intent;
  if (result.status === "invalid") return null;
  if (sources.abandoned) return null;

  // The in-memory copy outranks storage. `unpersisted` exists ONLY where the session storage
  // write was refused, and a refusal does not empty the store — a quota-exceeded tab throws
  // on setItem while every earlier entry stays readable. Reading storage first there redeems
  // that earlier item, under its coupon.
  return sources.unpersisted ?? getReorderIntent(sources.identity);
}

/**
 * The carry reorder params in Auth0 returnTo ONLY when sessionStorage not possible.
 * On that path the URL is the last copy in existence, and stripping it sends the
 * visitor to login with nothing to come back to.
 */
function buildLoginReturnTo(pathname: string, searchParams: URLSearchParams, unpersisted: UnpersistedIntent | null): string {
  if (!unpersisted) return toSafeStrippedPath(pathname, searchParams, REORDER_STRIP_PARAMS);
  return toSafeStrippedPath(pathname, withReorderBounceParams(searchParams, unpersisted.intent), KEEP_ALL_PARAMS);
}

/**
 * Drives a one-click reorder link end to end: detect → persist → strip → (login) → reorder → redirect.
 *
 * @returns what the mount point should show for this flow right now.
 */
export function useOneClickReorderFlow() {
  const $ = _c(16);
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const authResolved = useServicesStore(_temp);
  const hasSession = useAuthClaimsStore(_temp2);
  const sgCustomerId = useAuthClaimsStore(_temp3);
  const setIsCartOpen = useNewCartStore(_temp4);
  const {
    mutateAsync
  } = useOneClickReorder();
  const strippedKeyRef = useRef(null);
  const firedItemIdRef = useRef(null);
  const abandonedRef = useRef(false);
  const unpersistedIntentRef = useRef(null);
  const [status, setStatus] = useState("idle");
  let t0;
  let t1;
  if ($[0] !== pathname || $[1] !== router || $[2] !== searchParams || $[3] !== sgCustomerId) {
    t0 = () => {
      const result = parseReorderParams(searchParams);
      if (result.status === "absent") {
        return;
      }
      const key = result.status === "valid" ? result.intent.originalItemId : searchParams.toString();
      if (strippedKeyRef.current === key) {
        return;
      }
      strippedKeyRef.current = key;
      if (result.status === "invalid") {
        abandonedRef.current = true;
      } else {
        abandonedRef.current = false;
        setStatus("preparing");
        const persisted = setReorderIntent(result.intent, sgCustomerId ?? null);
        const bounced = hasReorderBounceParam(searchParams);
        unpersistedIntentRef.current = persisted ? null : {
          intent: result.intent,
          bounced
        };
      }
      router.replace(toSafeStrippedPath(pathname, searchParams, REORDER_STRIP_PARAMS));
    };
    t1 = [searchParams, pathname, router, sgCustomerId];
    $[0] = pathname;
    $[1] = router;
    $[2] = searchParams;
    $[3] = sgCustomerId;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[6] !== authResolved || $[7] !== hasSession || $[8] !== mutateAsync || $[9] !== pathname || $[10] !== router || $[11] !== searchParams || $[12] !== setIsCartOpen || $[13] !== sgCustomerId) {
    t2 = () => {
      if (!authResolved) {
        return;
      }
      const unpersisted = unpersistedIntentRef.current;
      const intent = selectIntent(parseReorderParams(searchParams), {
        abandoned: abandonedRef.current,
        identity: sgCustomerId ?? null,
        unpersisted: unpersisted?.intent ?? null
      });
      if (!intent || firedItemIdRef.current === intent.originalItemId) {
        return;
      }
      firedItemIdRef.current = intent.originalItemId;
      const hasBounced = unpersisted ? unpersisted.bounced : hasBouncedReorderIntent();
      const gate = resolveAuthGate(hasSession, sgCustomerId, hasBounced);
      if (gate === "bounce") {
        markReorderIntentBounced();
        const returnTo = buildLoginReturnTo(pathname, searchParams, unpersisted);
        window.location.assign(`/auth/login?returnTo=${encodeURIComponent(returnTo)}`);
        return;
      }
      if (gate === "giveUp") {
        clearReorderIntent();
        setStatus("failed");
        console.error("One-click reorder: no customer record for this session.");
        return;
      }
      setStatus("preparing");
      clearReorderIntent();
      mutateAsync(intent).then(cart => {
        setIsCartOpen(false);
        router.replace(`/store/checkout/${cart.id}?buyNow=true`);
      }).catch(error => {
        console.error(error);
        setStatus("failed");
      });
    };
    t3 = [searchParams, authResolved, hasSession, sgCustomerId, pathname, router, mutateAsync, setIsCartOpen];
    $[6] = authResolved;
    $[7] = hasSession;
    $[8] = mutateAsync;
    $[9] = pathname;
    $[10] = router;
    $[11] = searchParams;
    $[12] = setIsCartOpen;
    $[13] = sgCustomerId;
    $[14] = t2;
    $[15] = t3;
  } else {
    t2 = $[14];
    t3 = $[15];
  }
  useEffect(t2, t3);
  return status;
}
function _temp4(state_2) {
  return state_2.setIsCartOpen;
}
function _temp3(state_1) {
  return state_1.claims?.sg_customer_id;
}
function _temp2(state_0) {
  return state_0.claims !== undefined;
}
function _temp(state) {
  return state.authResolved;
}